import * as z from "zod";
import { HappyReturnsWebhookType } from "./webhooks";

export enum HappyReturnsAuditEvent {
  PENDING_AUDIT = "pending_audit",
  PASSED_AUDIT = "passed_audit",
  FAILED_AUDIT = "failed_audit",
}

export enum HappyReturnsReturnStatus {
  PARTIAL_REFUND = "partial-refund",
  COMPLETE = "complete",
  DROPPED_OFF = "dropped-off",
}

const returnWebhookItemSchema = z
  .object({
    approvedAt: z.string().nullish(),
    droppedOffAt: z.string().nullish(),
    externalRMAID: z.string().optional(),
    happyReturnsExpressCode: z.string(),
    happyReturnsItemID: z.string(),
    happyReturnsReturnID: z.string(),
    orderLineItemID: z.string(),
  })
  .passthrough();

const returnWebhookSchema = z
  .object({
    event: z.string(),
    typeID: z.string(),
    retailerID: z.string(),
    otherEvents: z
      .array(z.object({ event: z.string(), typeID: z.string() }).passthrough())
      .optional(),
    items: z.array(returnWebhookItemSchema),
  })
  .passthrough();

const returnAuditItemSchema = z
  .object({
    auditCompletedAt: z.string().nullish(),
    auditStatus: z.enum(HappyReturnsAuditEvent),
    happyReturnsItemID: z.string(),
    inspectionImageURLs: z.array(z.string()).optional(),
    orderID: z.string().optional(),
    orderLineItemID: z.string(),
    orderNumber: z.string().optional(),
    productName: z.string().optional(),
    sku: z.string().optional(),
    upc: z.string().optional(),
  })
  .passthrough();

const returnAuditWebhookSchema = z
  .object({
    event: z.enum(HappyReturnsAuditEvent),
    retailerID: z.string(),
    happyReturnsReturnID: z.string(),
    confirmationCode: z.string(),
    riskJustifications: z.array(z.string()).max(4).optional(),
    items: z.array(returnAuditItemSchema),
  })
  .passthrough();

const returnShipmentWebhookSchema = z
  .object({
    event: z.enum(["departed_from_return_bar", "received_at_hub"]),
    retailerID: z.string(),
    expressCode: z.string(),
    returnID: z.string(),
  })
  .passthrough();

const outboundShipmentWebhookSchema = z
  .object({
    event: z.enum(["outbound_shipment_departure", "outbound_shipment_arrival"]),
    retailerID: z.string(),
    id: z.number().int(),
  })
  .passthrough();

export type HappyReturnsWebhookPayload =
  | z.infer<typeof returnWebhookSchema>
  | z.infer<typeof returnAuditWebhookSchema>
  | z.infer<typeof returnShipmentWebhookSchema>
  | z.infer<typeof outboundShipmentWebhookSchema>;

export type HappyReturnsReturnWebhookPayload = z.infer<
  typeof returnWebhookSchema
>;
export type HappyReturnsReturnAuditWebhookPayload = z.infer<
  typeof returnAuditWebhookSchema
>;

export function parseHappyReturnsReturnWebhookPayload(
  payload: unknown,
): HappyReturnsReturnWebhookPayload {
  return returnWebhookSchema.parse(payload);
}

export function parseHappyReturnsReturnAuditWebhookPayload(
  payload: unknown,
): HappyReturnsReturnAuditWebhookPayload {
  return returnAuditWebhookSchema.parse(payload);
}

export function parseHappyReturnsWebhookPayload(
  typeID: HappyReturnsWebhookType,
  payload: unknown,
): HappyReturnsWebhookPayload {
  switch (typeID) {
    case HappyReturnsWebhookType.RETURN:
      return returnWebhookSchema.parse(payload);
    case HappyReturnsWebhookType.RETURN_AUDIT:
      return returnAuditWebhookSchema.parse(payload);
    case HappyReturnsWebhookType.RETURN_SHIPMENT:
      return returnShipmentWebhookSchema.parse(payload);
    case HappyReturnsWebhookType.OUTBOUND_SHIPMENT:
      return outboundShipmentWebhookSchema.parse(payload);
  }
}

export function isHappyReturnsWebhookType(
  value: string,
): value is HappyReturnsWebhookType {
  switch (value) {
    case HappyReturnsWebhookType.RETURN:
    case HappyReturnsWebhookType.RETURN_AUDIT:
    case HappyReturnsWebhookType.RETURN_SHIPMENT:
    case HappyReturnsWebhookType.OUTBOUND_SHIPMENT:
      return true;
    default:
      return false;
  }
}
